"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { AudioLines, MessageSquare, ChevronUp, LogOut, ExternalLink, Search, Plus, Users, ImagePlus, Film, PenTool } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrgShares } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { getChatUrl, getMarketingUrl } from "@llmgateway/shared/branding";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrgSearchDialog } from "./org-search-dialog";
import { OrganizationSwitcher } from "./organization-switcher";
import type { Organization } from "@/lib/types";
type Translate = ReturnType<typeof useI18n>["t"];
interface OrgSidebarProps {
  organizationId: string;
  currentShareId?: string;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (org: Organization | null) => void;
  className?: string;
}
interface OrgShareItem {
  id: string;
  title: string;
  model: string;
  createdAt: string;
  updatedAt: string;
}
type OrgShareHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "share";
  key: string;
  share: OrgShareItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 32;
const ROW_HEIGHT_SPACER = 16;
const ROW_HEIGHT_SHARE = 60;
interface OrgShareHistoryRowProps {
  rows: OrgShareHistoryRow[];
  currentShareId?: string;
  onShareSelect: (shareId: string) => void;
  t: Translate;
}
function getOrgShareRowHeight(index: number, {
  rows
}: OrgShareHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_SHARE;
}
function formatDate(dateString: string, t: Translate): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return t("time.justNow");
  } else if (diffInHours < 24) {
    return t("time.hoursAgo", {
      count: Math.floor(diffInHours)
    });
  } else if (diffInHours < 48) {
    return t("time.yesterday");
  } else {
    return format(date, "MMM d");
  }
}
function groupSharesByDate(shares: OrgShareItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as OrgShareItem[],
    yesterday: [] as OrgShareItem[],
    lastWeek: [] as OrgShareItem[],
    older: [] as OrgShareItem[]
  };
  shares.forEach(share => {
    const shareDate = new Date(share.updatedAt);
    if (shareDate.toDateString() === today.toDateString()) {
      groups.today.push(share);
    } else if (shareDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(share);
    } else if (shareDate >= lastWeek) {
      groups.lastWeek.push(share);
    } else {
      groups.older.push(share);
    }
  });
  return groups;
}
function OrgShareRowComponent(t0) {
  const $ = _c(30);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentShareId,
    onShareSelect,
    t
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    share
  } = row;
  const t1 = currentShareId === share.id;
  let t2;
  if ($[8] !== onShareSelect || $[9] !== share.id) {
    t2 = () => onShareSelect(share.id);
    $[8] = onShareSelect;
    $[9] = share.id;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== share.title) {
    t4 = <div className="truncate text-sm font-medium mb-0.5">{share.title}</div>;
    $[12] = share.title;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== share.updatedAt || $[15] !== t) {
    t5 = formatDate(share.updatedAt, t);
    $[14] = share.updatedAt;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <div className="text-xs text-muted-foreground">{t5}</div>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t6) {
    t7 = <div className="flex-1 min-w-0">{t4}{t6}</div>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t1 || $[23] !== t2 || $[24] !== t7) {
    t8 = <div className="relative h-full px-2 pb-1"><div className="relative h-full"><SidebarMenuButton isActive={t1} onClick={t2} className="h-full! w-full justify-start gap-3 group relative" type="button">{t3}{t7}</SidebarMenuButton></div></div>;
    $[22] = t1;
    $[23] = t2;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== ariaAttributes || $[27] !== style || $[28] !== t8) {
    t9 = <div {...ariaAttributes} style={style}>{t8}</div>;
    $[26] = ariaAttributes;
    $[27] = style;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
export function OrgSidebar(t0) {
  const $ = _c(250);
  const {
    organizationId,
    currentShareId,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className
  } = t0;
  const {
    t
  } = useI18n();
  const listContainerRef = useRef(null);
  const queryClient = useQueryClient();
  const router = useRouter();
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    isLoading: isOrgLoading
  } = useOrganization();
  const {
    data: orgSharesData,
    isLoading: isSharesLoading
  } = useOrgShares(organizationId);
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t1;
  if ($[0] !== currentTheme || $[1] !== setTheme) {
    t1 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[0] = currentTheme;
    $[1] = setTheme;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleTheme = t1;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);
  orgSharesData?.shares;
  let t2;
  if ($[3] !== orgSharesData?.shares) {
    t2 = orgSharesData?.shares ?? [];
    $[3] = orgSharesData?.shares;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const shares = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsMac(/(Mac|iPhone|iPad|iPod)/i.test(window.navigator.platform));
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isMac) {
    t5 = () => {
      const handleKeyDown = event => {
        const key = event.key.toLowerCase();
        const isSearchShortcut = isMac ? event.metaKey && key === "k" && !event.altKey && !event.ctrlKey : event.altKey && key === "k" && !event.metaKey && !event.ctrlKey;
        if (!isSearchShortcut || event.shiftKey || event.defaultPrevented) {
          return;
        }
        event.preventDefault();
        setIsSearchOpen(true);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t6 = [isMac];
    $[7] = isMac;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== posthog || $[11] !== queryClient || $[12] !== router || $[13] !== signOut) {
    t7 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : getChatUrl("/login"));
          }
        }
      });
    };
    $[10] = posthog;
    $[11] = queryClient;
    $[12] = router;
    $[13] = signOut;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const logout = t7;
  let t8;
  if ($[15] !== organizationId || $[16] !== router) {
    t8 = shareId => {
      router.push(`/org/${organizationId}/chat/${shareId}`);
    };
    $[15] = organizationId;
    $[16] = router;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleShareSelect = t8;
  let t9;
  if ($[18] !== shares) {
    t9 = groupSharesByDate([...shares].sort(_temp));
    $[18] = shares;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const shareGroups = t9;
  let rows;
  if ($[20] !== shareGroups.lastWeek || $[21] !== shareGroups.older || $[22] !== shareGroups.today || $[23] !== shareGroups.yesterday || $[24] !== t) {
    const groups = [{
      title: t("playground.today"),
      shares: shareGroups.today
    }, {
      title: t("playground.yesterday"),
      shares: shareGroups.yesterday
    }, {
      title: t("playground.last7Days"),
      shares: shareGroups.lastWeek
    }, {
      title: t("playground.older"),
      shares: shareGroups.older
    }];
    rows = [];
    groups.forEach((t10, groupIndex) => {
      const {
        title,
        shares: groupedShares
      } = t10;
      if (groupedShares.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedShares.forEach(share => {
        rows.push({
          type: "share",
          key: `share-${share.id}`,
          share
        });
      });
      const hasNextGroupWithShares = groups.slice(groupIndex + 1).some(_temp2);
      if (hasNextGroupWithShares) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[20] = shareGroups.lastWeek;
    $[21] = shareGroups.older;
    $[22] = shareGroups.today;
    $[23] = shareGroups.yesterday;
    $[24] = t;
    $[25] = rows;
  } else {
    rows = $[25];
  }
  const historyRows = rows;
  let t10;
  if ($[26] !== currentShareId || $[27] !== handleShareSelect || $[28] !== historyRows || $[29] !== t) {
    t10 = {
      rows: historyRows,
      currentShareId,
      onShareSelect: handleShareSelect,
      t
    };
    $[26] = currentShareId;
    $[27] = handleShareSelect;
    $[28] = historyRows;
    $[29] = t;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const rowProps = t10;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t11;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <ChatSidebarSkeleton organization={null} isOrgLoading={true} />;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    return t11;
  }
  if (!user) {
    let t11;
    let t12;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Logo className="size-6" />;
      t12 = <h1 className="text-xl font-semibold">doteb</h1>;
      $[32] = t11;
      $[33] = t12;
    } else {
      t11 = $[32];
      t12 = $[33];
    }
    let t13;
    if ($[34] !== t) {
      t13 = t("playground.chat");
      $[34] = t;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== t13) {
      t14 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t11}{t12}<Badge>{t13}</Badge></Link>;
      $[36] = t13;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    let t15;
    if ($[38] !== t) {
      t15 = t("playground.signInRequired");
      $[38] = t;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] !== t15) {
      t16 = <div className="font-medium mb-2">{t15}</div>;
      $[40] = t15;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== t) {
      t17 = t("playground.orgSharesSignInDescription");
      $[42] = t;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    let t18;
    if ($[44] !== t17) {
      t18 = <p className="text-muted-foreground mb-3">{t17}</p>;
      $[44] = t17;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== t) {
      t19 = t("auth.signIn");
      $[46] = t;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    let t20;
    if ($[48] !== t19) {
      t20 = <Button size="sm" asChild={true}><Link href="/login">{t19}</Link></Button>;
      $[48] = t19;
      $[49] = t20;
    } else {
      t20 = $[49];
    }
    let t21;
    if ($[50] !== t) {
      t21 = t("auth.createAccount");
      $[50] = t;
      $[51] = t21;
    } else {
      t21 = $[51];
    }
    let t22;
    if ($[52] !== t21) {
      t22 = <Button size="sm" variant="outline" asChild={true}><Link href="/signup">{t21}</Link></Button>;
      $[52] = t21;
      $[53] = t22;
    } else {
      t22 = $[53];
    }
    let t23;
    if ($[54] !== t20 || $[55] !== t22) {
      t23 = <div className="flex items-center justify-end gap-2">{t20}{t22}</div>;
      $[54] = t20;
      $[55] = t22;
      $[56] = t23;
    } else {
      t23 = $[56];
    }
    let t24;
    if ($[57] !== t16 || $[58] !== t18 || $[59] !== t23) {
      t24 = <div className="w-full rounded-md border p-4 text-sm">{t16}{t18}{t23}</div>;
      $[57] = t16;
      $[58] = t18;
      $[59] = t23;
      $[60] = t24;
    } else {
      t24 = $[60];
    }
    let t25;
    if ($[61] !== t14 || $[62] !== t24) {
      t25 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t14}{t24}</div></SidebarHeader>;
      $[61] = t14;
      $[62] = t24;
      $[63] = t25;
    } else {
      t25 = $[63];
    }
    let t26;
    if ($[64] !== className || $[65] !== t25) {
      t26 = <Sidebar className={className}>{t25}</Sidebar>;
      $[64] = className;
      $[65] = t25;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    return t26;
  }
  if (isSharesLoading || isOrgLoading) {
    let t11;
    if ($[67] !== isOrgLoading || $[68] !== selectedOrganization) {
      t11 = <ChatSidebarSkeleton organization={selectedOrganization} isOrgLoading={isOrgLoading} />;
      $[67] = isOrgLoading;
      $[68] = selectedOrganization;
      $[69] = t11;
    } else {
      t11 = $[69];
    }
    return t11;
  }
  let t11;
  if ($[70] !== className) {
    t11 = cn(className, "max-md:hidden");
    $[70] = className;
    $[71] = t11;
  } else {
    t11 = $[71];
  }
  let t12;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="doteb"><Link href="/" prefetch={true}><div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div><span className="text-lg font-bold tracking-tight">doteb</span></Link></SidebarMenuButton></SidebarMenuItem>;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  let t13;
  if ($[73] !== t) {
    t13 = t("playground.newChat");
    $[73] = t;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  let t14;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Plus className="h-4 w-4" />;
    $[75] = t14;
  } else {
    t14 = $[75];
  }
  let t15;
  if ($[76] !== t) {
    t15 = t("playground.newChat");
    $[76] = t;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  let t16;
  if ($[78] !== t15) {
    t16 = <Link href="/">{t14}<span>{t15}</span></Link>;
    $[78] = t15;
    $[79] = t16;
  } else {
    t16 = $[79];
  }
  let t17;
  if ($[80] !== t13 || $[81] !== t16) {
    t17 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t13} className="border border-border">{t16}</SidebarMenuButton></SidebarMenuItem>;
    $[80] = t13;
    $[81] = t16;
    $[82] = t17;
  } else {
    t17 = $[82];
  }
  let t18;
  if ($[83] !== t) {
    t18 = t("playground.chat");
    $[83] = t;
    $[84] = t18;
  } else {
    t18 = $[84];
  }
  let t19;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <MessageSquare className="h-4 w-4" />;
    $[85] = t19;
  } else {
    t19 = $[85];
  }
  let t20;
  if ($[86] !== t) {
    t20 = t("playground.chat");
    $[86] = t;
    $[87] = t20;
  } else {
    t20 = $[87];
  }
  let t21;
  if ($[88] !== t20) {
    t21 = <Link href="/" prefetch={true}>{t19}<span>{t20}</span></Link>;
    $[88] = t20;
    $[89] = t21;
  } else {
    t21 = $[89];
  }
  let t22;
  if ($[90] !== t18 || $[91] !== t21) {
    t22 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t18}>{t21}</SidebarMenuButton></SidebarMenuItem>;
    $[90] = t18;
    $[91] = t21;
    $[92] = t22;
  } else {
    t22 = $[92];
  }
  let t23;
  if ($[93] !== t) {
    t23 = t("playground.groupChat");
    $[93] = t;
    $[94] = t23;
  } else {
    t23 = $[94];
  }
  let t24;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Users className="h-4 w-4" />;
    $[95] = t24;
  } else {
    t24 = $[95];
  }
  let t25;
  if ($[96] !== t) {
    t25 = t("playground.groupChat");
    $[96] = t;
    $[97] = t25;
  } else {
    t25 = $[97];
  }
  let t26;
  if ($[98] !== t25) {
    t26 = <Link href="/group" prefetch={true}>{t24}<span>{t25}</span></Link>;
    $[98] = t25;
    $[99] = t26;
  } else {
    t26 = $[99];
  }
  let t27;
  if ($[100] !== t23 || $[101] !== t26) {
    t27 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t23}>{t26}</SidebarMenuButton></SidebarMenuItem>;
    $[100] = t23;
    $[101] = t26;
    $[102] = t27;
  } else {
    t27 = $[102];
  }
  let t28;
  if ($[103] !== t) {
    t28 = t("playground.imageStudio");
    $[103] = t;
    $[104] = t28;
  } else {
    t28 = $[104];
  }
  let t29;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ImagePlus className="h-4 w-4" />;
    $[105] = t29;
  } else {
    t29 = $[105];
  }
  let t30;
  if ($[106] !== t) {
    t30 = t("playground.imageStudio");
    $[106] = t;
    $[107] = t30;
  } else {
    t30 = $[107];
  }
  let t31;
  if ($[108] !== t30) {
    t31 = <Link href="/image" prefetch={true}>{t29}<span>{t30}</span></Link>;
    $[108] = t30;
    $[109] = t31;
  } else {
    t31 = $[109];
  }
  let t32;
  if ($[110] !== t28 || $[111] !== t31) {
    t32 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t28}>{t31}</SidebarMenuButton></SidebarMenuItem>;
    $[110] = t28;
    $[111] = t31;
    $[112] = t32;
  } else {
    t32 = $[112];
  }
  let t33;
  if ($[113] !== t) {
    t33 = t("playground.videoStudio");
    $[113] = t;
    $[114] = t33;
  } else {
    t33 = $[114];
  }
  let t34;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Film className="h-4 w-4" />;
    $[115] = t34;
  } else {
    t34 = $[115];
  }
  let t35;
  if ($[116] !== t) {
    t35 = t("playground.videoStudio");
    $[116] = t;
    $[117] = t35;
  } else {
    t35 = $[117];
  }
  let t36;
  if ($[118] !== t35) {
    t36 = <Link href="/video" prefetch={true}>{t34}<span>{t35}</span></Link>;
    $[118] = t35;
    $[119] = t36;
  } else {
    t36 = $[119];
  }
  let t37;
  if ($[120] !== t33 || $[121] !== t36) {
    t37 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t33}>{t36}</SidebarMenuButton></SidebarMenuItem>;
    $[120] = t33;
    $[121] = t36;
    $[122] = t37;
  } else {
    t37 = $[122];
  }
  let t38;
  if ($[123] !== t) {
    t38 = t("playground.audioStudio");
    $[123] = t;
    $[124] = t38;
  } else {
    t38 = $[124];
  }
  let t39;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <AudioLines className="h-4 w-4" />;
    $[125] = t39;
  } else {
    t39 = $[125];
  }
  let t40;
  if ($[126] !== t) {
    t40 = t("playground.audioStudio");
    $[126] = t;
    $[127] = t40;
  } else {
    t40 = $[127];
  }
  let t41;
  if ($[128] !== t40) {
    t41 = <Link href="/audio" prefetch={true}>{t39}<span>{t40}</span></Link>;
    $[128] = t40;
    $[129] = t41;
  } else {
    t41 = $[129];
  }
  let t42;
  if ($[130] !== t38 || $[131] !== t41) {
    t42 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t38}>{t41}</SidebarMenuButton></SidebarMenuItem>;
    $[130] = t38;
    $[131] = t41;
    $[132] = t42;
  } else {
    t42 = $[132];
  }
  let t43;
  if ($[133] !== t) {
    t43 = t("playground.canvas");
    $[133] = t;
    $[134] = t43;
  } else {
    t43 = $[134];
  }
  let t44;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <PenTool className="h-4 w-4" />;
    $[135] = t44;
  } else {
    t44 = $[135];
  }
  let t45;
  if ($[136] !== t) {
    t45 = t("playground.canvas");
    $[136] = t;
    $[137] = t45;
  } else {
    t45 = $[137];
  }
  let t46;
  if ($[138] !== t45) {
    t46 = <Link href="/canvas" prefetch={true}>{t44}<span>{t45}</span></Link>;
    $[138] = t45;
    $[139] = t46;
  } else {
    t46 = $[139];
  }
  let t47;
  if ($[140] !== t43 || $[141] !== t46) {
    t47 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t43}>{t46}</SidebarMenuButton></SidebarMenuItem>;
    $[140] = t43;
    $[141] = t46;
    $[142] = t47;
  } else {
    t47 = $[142];
  }
  let t48;
  if ($[143] !== t17 || $[144] !== t22 || $[145] !== t27 || $[146] !== t32 || $[147] !== t37 || $[148] !== t42 || $[149] !== t47) {
    t48 = <SidebarHeader><SidebarMenu>{t12}{t17}{t22}{t27}{t32}{t37}{t42}{t47}</SidebarMenu></SidebarHeader>;
    $[143] = t17;
    $[144] = t22;
    $[145] = t27;
    $[146] = t32;
    $[147] = t37;
    $[148] = t42;
    $[149] = t47;
    $[150] = t48;
  } else {
    t48 = $[150];
  }
  let t49;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[151] = t49;
  } else {
    t49 = $[151];
  }
  let t50;
  if ($[152] !== onSelectOrganization || $[153] !== organizations || $[154] !== selectedOrganization) {
    t50 = organizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[152] = onSelectOrganization;
    $[153] = organizations;
    $[154] = selectedOrganization;
    $[155] = t50;
  } else {
    t50 = $[155];
  }
  let t51;
  if ($[156] !== t) {
    t51 = t("playground.searchChats");
    $[156] = t;
    $[157] = t51;
  } else {
    t51 = $[157];
  }
  let t52;
  let t53;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = () => setIsSearchOpen(true);
    t53 = <Search className="h-4 w-4" />;
    $[158] = t52;
    $[159] = t53;
  } else {
    t52 = $[158];
    t53 = $[159];
  }
  let t54;
  if ($[160] !== t) {
    t54 = t("playground.searchChats");
    $[160] = t;
    $[161] = t54;
  } else {
    t54 = $[161];
  }
  let t55;
  if ($[162] !== t54) {
    t55 = <span>{t54}</span>;
    $[162] = t54;
    $[163] = t55;
  } else {
    t55 = $[163];
  }
  const t56 = isMac ? "\u2318K" : "Alt+K";
  let t57;
  if ($[164] !== t56) {
    t57 = <kbd className="ml-auto text-xs font-medium text-muted-foreground opacity-0 transition-opacity group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100 group-data-[collapsible=icon]:hidden">{t56}</kbd>;
    $[164] = t56;
    $[165] = t57;
  } else {
    t57 = $[165];
  }
  let t58;
  if ($[166] !== t51 || $[167] !== t55 || $[168] !== t57) {
    t58 = <SidebarMenu className="px-2"><SidebarMenuItem><SidebarMenuButton type="button" tooltip={t51} onClick={t52}>{t53}{t55}{t57}</SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[166] = t51;
    $[167] = t55;
    $[168] = t57;
    $[169] = t58;
  } else {
    t58 = $[169];
  }
  let t59;
  if ($[170] !== t50 || $[171] !== t58) {
    t59 = <div>{t49}{t50}{t58}</div>;
    $[170] = t50;
    $[171] = t58;
    $[172] = t59;
  } else {
    t59 = $[172];
  }
  let t60;
  if ($[173] !== historyRows || $[174] !== rowProps || $[175] !== shares.length || $[176] !== t) {
    t60 = shares.length === 0 ? <div className="flex flex-col items-center justify-center py-8 px-4 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">{t("playground.noSharedChats")}</p><p className="text-xs text-muted-foreground">{t("playground.noSharedChatsDescription")}</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={OrgShareRowComponent} rowCount={historyRows.length} rowHeight={getOrgShareRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[173] = historyRows;
    $[174] = rowProps;
    $[175] = shares.length;
    $[176] = t;
    $[177] = t60;
  } else {
    t60 = $[177];
  }
  let t61;
  if ($[178] !== isHistoryHidden || $[179] !== t60) {
    t61 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t60}</div>;
    $[178] = isHistoryHidden;
    $[179] = t60;
    $[180] = t61;
  } else {
    t61 = $[180];
  }
  let t62;
  if ($[181] !== t59 || $[182] !== t61) {
    t62 = <SidebarContent className="overflow-hidden pb-2">{t59}{t61}</SidebarContent>;
    $[181] = t59;
    $[182] = t61;
    $[183] = t62;
  } else {
    t62 = $[183];
  }
  let t63;
  if ($[184] !== isOrgLoading || $[185] !== selectedOrganization) {
    t63 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={selectedOrganization} isLoading={isOrgLoading} /></div>;
    $[184] = isOrgLoading;
    $[185] = selectedOrganization;
    $[186] = t63;
  } else {
    t63 = $[186];
  }
  let t64;
  if ($[187] !== t || $[188] !== user?.name) {
    t64 = user?.name ?? t("playground.user");
    $[187] = t;
    $[188] = user?.name;
    $[189] = t64;
  } else {
    t64 = $[189];
  }
  let t65;
  if ($[190] !== user?.name) {
    t65 = user?.name?.split(" ").map(_temp3).join("").toUpperCase().slice(0, 2) ?? "U";
    $[190] = user?.name;
    $[191] = t65;
  } else {
    t65 = $[191];
  }
  let t66;
  if ($[192] !== t65) {
    t66 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t65}</span></div>;
    $[192] = t65;
    $[193] = t66;
  } else {
    t66 = $[193];
  }
  const t67 = user?.name;
  let t68;
  if ($[194] !== t67) {
    t68 = <span className="truncate font-semibold">{t67}</span>;
    $[194] = t67;
    $[195] = t68;
  } else {
    t68 = $[195];
  }
  const t69 = user?.email;
  let t70;
  if ($[196] !== t69) {
    t70 = <span className="truncate text-xs text-muted-foreground">{t69}</span>;
    $[196] = t69;
    $[197] = t70;
  } else {
    t70 = $[197];
  }
  let t71;
  if ($[198] !== t68 || $[199] !== t70) {
    t71 = <div className="grid flex-1 text-left text-sm leading-tight">{t68}{t70}</div>;
    $[198] = t68;
    $[199] = t70;
    $[200] = t71;
  } else {
    t71 = $[200];
  }
  let t72;
  if ($[201] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[201] = t72;
  } else {
    t72 = $[201];
  }
  let t73;
  if ($[202] !== t64 || $[203] !== t66 || $[204] !== t71) {
    t73 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t64} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t66}{t71}{t72}</SidebarMenuButton></DropdownMenuTrigger>;
    $[202] = t64;
    $[203] = t66;
    $[204] = t71;
    $[205] = t73;
  } else {
    t73 = $[205];
  }
  let t74;
  if ($[206] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : getMarketingUrl("/dashboard");
    $[206] = t74;
  } else {
    t74 = $[206];
  }
  let t75;
  if ($[207] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <ExternalLink className="mr-2 h-4 w-4" />;
    $[207] = t75;
  } else {
    t75 = $[207];
  }
  let t76;
  if ($[208] !== t) {
    t76 = t("playground.dashboard");
    $[208] = t;
    $[209] = t76;
  } else {
    t76 = $[209];
  }
  let t77;
  if ($[210] !== t76) {
    t77 = <DropdownMenuItem asChild={true}><a href={t74} target="_blank" rel="noopener noreferrer">{t75}{t76}</a></DropdownMenuItem>;
    $[210] = t76;
    $[211] = t77;
  } else {
    t77 = $[211];
  }
  let t78;
  if ($[212] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = <DropdownMenuSeparator />;
    $[212] = t78;
  } else {
    t78 = $[212];
  }
  let t79;
  if ($[213] !== toggleTheme) {
    t79 = event_0 => {
      event_0.preventDefault();
      toggleTheme();
    };
    $[213] = toggleTheme;
    $[214] = t79;
  } else {
    t79 = $[214];
  }
  let t80;
  if ($[215] !== t) {
    t80 = t("playground.theme");
    $[215] = t;
    $[216] = t80;
  } else {
    t80 = $[216];
  }
  let t81;
  if ($[217] !== t80) {
    t81 = <span>{t80}</span>;
    $[217] = t80;
    $[218] = t81;
  } else {
    t81 = $[218];
  }
  let t82;
  if ($[219] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = <div onClick={_temp4} onKeyDown={_temp5}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[219] = t82;
  } else {
    t82 = $[219];
  }
  let t83;
  if ($[220] !== t79 || $[221] !== t81) {
    t83 = <DropdownMenuItem className="justify-between gap-3" onSelect={t79}>{t81}{t82}</DropdownMenuItem>;
    $[220] = t79;
    $[221] = t81;
    $[222] = t83;
  } else {
    t83 = $[222];
  }
  let t84;
  if ($[223] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = <DropdownMenuSeparator />;
    $[223] = t84;
  } else {
    t84 = $[223];
  }
  let t85;
  if ($[224] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = <LogOut className="mr-2 h-4 w-4" />;
    $[224] = t85;
  } else {
    t85 = $[224];
  }
  let t86;
  if ($[225] !== t) {
    t86 = t("playground.logOut");
    $[225] = t;
    $[226] = t86;
  } else {
    t86 = $[226];
  }
  let t87;
  if ($[227] !== logout || $[228] !== t86) {
    t87 = <DropdownMenuItem onClick={logout}>{t85}{t86}</DropdownMenuItem>;
    $[227] = logout;
    $[228] = t86;
    $[229] = t87;
  } else {
    t87 = $[229];
  }
  let t88;
  if ($[230] !== t77 || $[231] !== t83 || $[232] !== t87) {
    t88 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t77}{t78}{t83}{t84}{t87}</DropdownMenuContent>;
    $[230] = t77;
    $[231] = t83;
    $[232] = t87;
    $[233] = t88;
  } else {
    t88 = $[233];
  }
  let t89;
  if ($[234] !== t73 || $[235] !== t88) {
    t89 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t73}{t88}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[234] = t73;
    $[235] = t88;
    $[236] = t89;
  } else {
    t89 = $[236];
  }
  let t90;
  if ($[237] !== t63 || $[238] !== t89) {
    t90 = <SidebarFooter>{t63}{t89}</SidebarFooter>;
    $[237] = t63;
    $[238] = t89;
    $[239] = t90;
  } else {
    t90 = $[239];
  }
  let t91;
  if ($[240] !== handleShareSelect || $[241] !== isSearchOpen || $[242] !== organizationId) {
    t91 = <OrgSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} organizationId={organizationId} onShareSelect={handleShareSelect} />;
    $[240] = handleShareSelect;
    $[241] = isSearchOpen;
    $[242] = organizationId;
    $[243] = t91;
  } else {
    t91 = $[243];
  }
  let t92;
  if ($[244] !== t11 || $[245] !== t48 || $[246] !== t62 || $[247] !== t90 || $[248] !== t91) {
    t92 = <Sidebar collapsible="icon" className={t11}>{t48}{t62}{t90}{t91}</Sidebar>;
    $[244] = t11;
    $[245] = t48;
    $[246] = t62;
    $[247] = t90;
    $[248] = t91;
    $[249] = t92;
  } else {
    t92 = $[249];
  }
  return t92;
}
function _temp5(event_2) {
  return event_2.stopPropagation();
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp3(n) {
  return n[0];
}
function _temp2(group) {
  return group.shares.length > 0;
}
function _temp(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}